import React from 'react'
import { Link } from 'react-router-dom'
import { tv } from 'tailwind-variants'
import { useProject } from 'hooks/useProject'
import { tint } from 'helpers/colour'
import { SECONDARY_TINT } from 'style/constants'
import Spinner from 'components/spinner'

// Sizing lives in the base rather than at the call site so buttons are
// consistent by default: 32px tall (h-8). Anything a caller passes in
// `className` still wins via tailwind-merge, but padding doesn't override a
// height: a button sized by its padding (py-2, p-2.5, …) also needs h-auto, or
// it's held at 32px whatever its padding says.
//
// Deliberately flat: no gradient, no inset highlight, no drop shadow, nothing
// moving on press. Elevation belongs to surfaces that genuinely float.
const buttonStyles = tv({
  base: [
    'select-none inline-flex items-center justify-center gap-1.5 shrink-0',
    'h-8 px-3 rounded-md text-[13px] font-medium leading-none tracking-[-0.006em]',
    'border border-transparent transition-colors duration-150',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue',
    'disabled:opacity-45 disabled:cursor-not-allowed disabled:pointer-events-none'
  ].join(' '),
  variants: {
    internalType: {
      // primary and secondary take their fill from the hub's brand colour via
      // an inline style further down, so they only set hover behaviour here.
      primary: 'hover:brightness-95',
      secondary: 'hover:brightness-95',
      outline: 'bg-surface text-body border-divider hover:bg-surface-sunken',
      danger: 'bg-red text-on-solid hover:brightness-95',
      accept: 'bg-green text-on-light hover:brightness-95',
      grey: 'bg-surface-sunken text-body hover:bg-surface-strong',
      gradient: 'bg-[linear-gradient(to_right,#afaee5_0%,#006dc0_40%)] text-on-solid hover:brightness-95',
      flashing: 'bg-alert text-on-light border-alert animate-pulse-alert',
      transparent: 'bg-transparent text-muted hover:bg-surface-sunken hover:text-body'
    }
  },
  defaultVariants: {
    internalType: 'primary'
  }
})

interface ButtonProps extends React.HTMLProps<HTMLButtonElement> {
  type?: 'button' | 'reset' | 'submit' | undefined
  internalType?:
    | 'primary'
    | 'outline'
    | 'secondary'
    | 'danger'
    | 'accept'
    | 'grey'
    | 'gradient'
    | 'flashing'
    | 'transparent'
  to?: string
  isLoading?: boolean
  innerRef?: React.Ref<HTMLButtonElement>
}

const Button = (props: ButtonProps) => {
  const {
    className = '',
    onClick,
    type = 'button',
    children,
    disabled,
    internalType = 'primary',
    to,
    isLoading,
    innerRef,
    ...rest
  } = props
  const { project } = useProject()

  const dynamicStyle: React.CSSProperties = {}
  if (internalType === 'primary') {
    dynamicStyle.backgroundColor = tint(project.primaryColour, SECONDARY_TINT)
    dynamicStyle.color = project.fontColour
  } else if (internalType === 'secondary') {
    dynamicStyle.backgroundColor = project.primaryColour
    dynamicStyle.color = project.fontColour
  }

  const btn = (
    <button
      ref={innerRef}
      className={buttonStyles({ internalType, class: className })}
      style={dynamicStyle}
      type={type}
      disabled={disabled || isLoading}
      onClick={onClick}
      {...rest}
    >
      {children}
      {isLoading && <Spinner size="small" className="ml-2.5" />}
    </button>
  )

  if (to) {
    return (
      <Link className="flex flex-none items-center" to={to}>
        {btn}
      </Link>
    )
  }

  return btn
}

export default Button
